/**
 * Townsy's public policies (founder request 2026-10-13: "set the terms up ... make a visible privacy policy, terms of
 * use, payments policy, etc (make it clear that any money paid into Townsy is not withdrawable or refundable)").
 *
 * DRAFTS pending legal review (docs/12 C3, C4): the operator's legal name, address, contact, governing law and tax
 * details are placeholders the founder fills in. Per docs/08 section 7 the terms do not claim "no refunds under any
 * circumstance": payments are final and not withdrawable, with the exceptions the law and Townsy's own rules require
 * (a payment that could not deliver what it paid for goes back to the card, never into Townsy Credit).
 */

export type LegalSlug = "terms" | "privacy" | "payments" | "advertisers" | "community";

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDoc {
  slug: LegalSlug;
  title: string;
  summary: string;
  sections: LegalSection[];
}

export const LEGAL_VERSION = "Draft of 4 November 2026 (pending legal review)";
export const OPERATOR = "Arlington North LLC";
export const OPERATOR_ADDRESS = "1621 Central Ave, Cheyenne, WY 82001, USA";
export const CONTACT_EMAIL = "support@townsy.io";

export const LEGAL_LINKS: Array<{ slug: LegalSlug; label: string }> = [
  { slug: "terms", label: "Terms of Use" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "payments", label: "Payments & Credit Policy" },
  { slug: "advertisers", label: "Advertiser Policy" },
  { slug: "community", label: "Community Rules" },
];

const terms: LegalDoc = {
  slug: "terms",
  title: "Terms of Use",
  summary: "The rules for using Townsy, owning virtual buildings and homes, and advertising in the town.",
  sections: [
    {
      heading: "1. Who we are and these terms",
      paragraphs: [
        `Townsy (townsy.io) is operated by ${OPERATOR}, ${OPERATOR_ADDRESS} ("Townsy", "we"). By visiting Townsy or creating an account you agree to these Terms, the Privacy Policy, the Payments & Credit Policy, the Advertiser Policy and the Community Rules. If you do not agree, do not use Townsy.`,
        "We may update these Terms. We will show the date of the current version on this page and, for important changes, tell signed-in users before they apply. Continuing to use Townsy after a change means you accept it.",
      ],
    },
    {
      heading: "2. Accounts and guests",
      paragraphs: [
        "You can visit as a guest with a randomly assigned name. An account lets you choose a display name, buy and keep virtual property, earn coins and send private messages. You must give a working email address and keep your password safe; you are responsible for activity on your account.",
        "Townsy accounts are only for adults. You must be 18 or older to create an account, and you confirm it when you sign up. Betting with play chips, buying, earning coins and messaging all need an account. We do not knowingly collect personal data from anyone under 18; if we learn that an account belongs to someone under 18, we close it.",
        "We may suspend or close accounts that break these Terms or the Community Rules, or to protect other users or Townsy.",
      ],
    },
    {
      heading: "3. Virtual buildings, homes, coins and credit",
      paragraphs: [
        "Everything you can own in Townsy (stores, plots, homes, furniture, signs, coins and Townsy Credit) is a limited, revocable licence to use a feature of Townsy. It is not real property, not a financial product and has no value outside Townsy.",
        "Commercial buildings can be acquired by another user at a higher price. When that happens, the previous owner receives Townsy Credit equal to 100% of their eligible acquisition price. Townsy Credit is not cash and cannot be withdrawn (see the Payments & Credit Policy).",
        "Residential plots and homes can never be taken over by another user.",
        "Coins and Townsy Credit are separate. They cannot be exchanged for each other, transferred to another user, sold, or converted into money.",
      ],
    },
    {
      heading: "4. Your content and conduct",
      paragraphs: [
        "You are responsible for what you post: chat, private messages, store boards, display names, logos, designs and website links. Follow the Community Rules and, for advertising, the Advertiser Policy. We may filter, hide or remove content and restrict accounts.",
        "You give Townsy a worldwide, non-exclusive, royalty-free licence to host, show and process the content you submit so Townsy can run (for example, showing your logo on your store).",
      ],
    },
    {
      heading: "5. AI residents",
      paragraphs: [
        "Some townspeople are AI residents, marked with an \"AI\" badge. They are computer programs, not people. Their answers can be wrong; do not rely on them for advice.",
      ],
    },
    {
      heading: "6. Availability, changes and test mode",
      paragraphs: [
        "Townsy is provided \"as is\". We may change, pause or end features, prices and the town itself. While Townsy shows \"TEST\", payments use Stripe test mode and data may be reset.",
      ],
    },
    {
      heading: "7. Liability",
      paragraphs: [
        "To the extent the law allows, Townsy is not liable for indirect or consequential losses, and our total liability to you is limited to the amount you paid Townsy in the 12 months before the claim. Nothing in these Terms limits rights you have under consumer law that cannot be limited.",
      ],
    },
    {
      heading: "8. Law and contact",
      paragraphs: [`These Terms are governed by the laws of the State of Wyoming, USA. Disputes go to the state or federal courts located in Wyoming, unless the law of your place of residence gives you a right to bring them elsewhere. Questions: ${CONTACT_EMAIL}.`],
    },
  ],
};

const privacy: LegalDoc = {
  slug: "privacy",
  title: "Privacy Policy",
  summary: "What Townsy collects, why, how long we keep it, and your choices.",
  sections: [
    {
      heading: "1. What we collect",
      paragraphs: [`${OPERATOR} is responsible for your personal data on Townsy. We collect:`],
      bullets: [
        "Account details: email address, display name, a protected (hashed) password, avatar choices and settings.",
        "Guest visits: a random guest identifier kept in a cookie.",
        "Approximate country from our network provider (Cloudflare) to show a flag. Never your exact location. Accounts can hide it.",
        "What you do in Townsy: rooms you visit, movement in rooms, purchases, coins and credit history, sponsored tasks, website clicks from Townsy, and pseudonymous visit statistics.",
        "What you write: chat (kept 7 days), private messages (kept 30 days), store boards, designs and uploads. Reported content is kept longer for review.",
        "Payments: handled by Stripe. We receive payment status and the last digits / brand of the card, never the full card number. For sales tax, the country and postal code you enter at checkout are sent to Stripe and kept with your order and receipt.",
        "Technical data: IP address and device information in security logs, kept only as long as needed to protect the service.",
      ],
    },
    {
      heading: "2. Why we use it",
      paragraphs: [
        "To run Townsy (accounts, rooms, purchases, receipts), keep it safe (moderation, abuse and fraud prevention), show honest statistics to advertisers without identifying you, send account and transaction emails, and measure whether our own ads on X bring visitors and purchases.",
      ],
    },
    {
      heading: "3. Who we share it with",
      paragraphs: ["We do not sell your personal data for money. We use service providers that process data for us, and we share some data with X to measure our ads:"],
      bullets: [
        "Stripe (payments), Mailgun (email), Cloudflare (network and security), Amazon Web Services (hosting).",
        "Anthropic or OpenAI, when enabled, to power AI residents' conversation and to check advertisers' designs and sponsored tasks. Only the text of your message to an AI resident is sent, never your identity.",
        "HitCounters.com and Teraforma (teraforma.com), a visit counter on our pages: they receive your IP address, browser and device details (such as screen size and time zone), the page you open and when, and how long it stays open, and they keep a visit identifier in your browser's storage. Townsy's own statistics are separate.",
        "X (formerly Twitter), to measure our ads on X. Where ad measurement is on (see section 6), X's script runs on the town and our public pages. It receives your IP address, browser and device details, the page you are on and the page you came from, how long the page stays open, the text of the buttons you click, and any email address (hashed), phone number or name in a form on that page. It keeps identifiers in your browser and on X's own domains. When you pay by card, our server also sends X the amount, the time, a hashed copy of your email address, and the click code from X's ad if you came from one. X handles this data under its own privacy policy and may link it to an X account and use it for its advertising. Under some US state privacy laws this may count as \"sharing\" or \"selling\" personal data; section 6 explains how to turn it off.",
        "Authorities, when the law requires it or to protect people from harm.",
      ],
    },
    {
      heading: "4. Moderation and private messages",
      paragraphs: [
        "Townsy moderators read a private conversation only if someone reports it. A report keeps a copy of the recent messages for review. Other users never learn who reported them.",
      ],
    },
    {
      heading: "5. Cookies",
      paragraphs: [
        "Townsy's necessary cookies are your session or guest identity, security, and (during the private beta) the beta access cookie. A few settings, such as the town map being open, are kept in your browser's storage. Our visit counter from HitCounters.com (see section 3) also keeps a visit identifier in your browser's storage.",
        "Where ad measurement is on (see section 6), these advertising cookies are also used: townsy_twclid keeps the click code from X's ad for 30 days, and X's script keeps its own identifiers for up to 13 months (_twclid, _twpid, _twsid, and cookies on X's domains). If you turn ad measurement off, townsy_ads_opt_out remembers that choice for a year.",
      ],
    },
    {
      heading: "6. Your choices and rights",
      paragraphs: [
        `You can change your display name, avatar, country display and who may message you on your Account page. You can also download your data and delete your account there (sections 7 and 8), or write to ${CONTACT_EMAIL}.`,
        "Ad measurement with X (section 3): we do not use it for visitors in the EU, the EEA, the UK or Switzerland, or anywhere we cannot tell your region. Everywhere else it is on unless you turn it off with the switch at the end of this page, or your browser sends the Global Privacy Control signal. The choice applies to this browser. X's script never runs on the account, order, password reset, review or admin pages.",
      ],
    },
    {
      heading: "7. A copy of your data",
      paragraphs: [
        'On your Account page, "Download my data" gives you one file (JSON) with what Townsy stores about your account: your profile and avatar, your coins and Townsy Credit and their history, your orders and payments (amounts and statuses), the buildings, home and items you own or owned, billboard rentals, your advertiser designs and offers, sponsored tasks you did, your private messages (the conversations you are part of), the reports you filed, the people you muted and your current sign-ins (device and time).',
        "It never contains other people's email addresses or private details, reports other people made about you, passwords or security keys. Other people appear only by the display name Townsy shows. You can download it up to 10 times a day.",
      ],
    },
    {
      heading: "8. Deleting your account",
      paragraphs: [
        'On your Account page, "Delete my account" first shows exactly what you would lose. You confirm with your password and by typing DELETE. Your account is deleted 7 days later. Until then it works as usual, and you can sign in and cancel at any time. We email you when you ask, if you cancel, and when it is done.',
        "If a payment, refund, dispute or a review by Townsy staff is still open on that day, we finish it first and delete your account afterwards. Your Account page shows why it is waiting.",
        "When your account is deleted:",
      ],
      bullets: [
        "Your stores and your home go back to the town for sale. Billboard rentals end early and your offers stop. There is no refund for them.",
        "Your coins and Townsy Credit are forfeited. They are not money and are never paid out.",
        "Any unused budget of sponsored tasks you paid for by card is forfeited too, with no refund. Tasks other people are already doing for you finish first.",
        "The logos and images you uploaded stop showing at once. We delete them from our storage 30 days after your account is deleted, unless they are part of a safety review that is still open; then we delete them when the review is closed.",
        "Your display name becomes \"Former resident\". Your email address, password, sign-ins, avatar and country are removed, and you cannot sign in again. The private messages and chat you sent are removed, except messages that are part of a safety report.",
        "We keep the records the law and safety require, with your name removed: payments, orders, receipts, refunds and disputes, the coin and credit history, who owned a building when, moderation reports and actions, and the record that you asked for the deletion. Visit statistics never identified you and stay as they are.",
        "Your email address can be used to open a new account later. A new account starts fresh.",
      ],
    },
    {
      heading: "9. Children",
      paragraphs: ["Townsy accounts are only for adults aged 18 or older. We do not knowingly collect personal data from anyone under 18; if we learn that we have, we delete it and close the account."],
    },
    {
      heading: "10. Contact",
      paragraphs: [`${OPERATOR}, ${OPERATOR_ADDRESS}. Email: ${CONTACT_EMAIL}.`],
    },
  ],
};

const payments: LegalDoc = {
  slug: "payments",
  title: "Payments & Credit Policy",
  summary: "Money paid into Townsy is final: it cannot be withdrawn and is not refunded, except in the cases listed below.",
  sections: [
    {
      heading: "1. Payments are final",
      paragraphs: [
        "When you pay Townsy (for a store, a plot, a home upgrade, an item or a billboard rental), the payment is final. Sponsored tasks are free for store owners; a task budget paid before they became free follows the same rule, and what is left of it is forfeited if you delete your account (Privacy Policy, section 8).",
        "Money paid into Townsy cannot be withdrawn, cashed out or transferred, and it is not refunded, except where a payment could not deliver what it paid for or where the law requires a refund (section 4).",
      ],
    },
    {
      heading: "2. Townsy Credit",
      paragraphs: ["Townsy Credit is a dollar-denominated balance you can spend only inside Townsy."],
      bullets: [
        "You receive Townsy Credit when another user takes over your store: 100% of your eligible acquisition price.",
        "Townsy Credit is not money. It cannot be withdrawn, cashed out, refunded, sold, transferred to another user, or converted into coins.",
        "It can pay for Townsy purchases where shown (it never pays for a sponsored-task budget, which no longer exists for new tasks).",
        "Townsy Credit does not expire while your account is open. Unused credit ends when your account is closed.",
      ],
    },
    {
      heading: "3. Coins",
      paragraphs: [
        "Coins are a separate virtual reward for playing and for completing sponsored tasks. They have no cash value, cannot be withdrawn, sold, transferred or converted into Townsy Credit, and end when your account is closed.",
      ],
    },
    {
      heading: "4. When we do refund to your card",
      paragraphs: ["Your card is refunded (never paid back in Townsy Credit) when:"],
      bullets: [
        "a payment went through but the purchase could not be completed (for example, someone else bought the store first);",
        "you were charged twice for the same order, or charged by mistake;",
        "the law of your country requires a refund.",
      ],
    },
    {
      heading: "5. Prices, taxes and receipts",
      paragraphs: [
        "Prices are shown before you pay and are charged in US dollars through Stripe. Prices do not include sales tax. Where sales tax applies, it is calculated at checkout from the country and postal code you enter, shown before you pay, paid by card and listed on your receipt. A full refund to your card includes the sales tax; a partial refund returns part of the price only, not the sales tax. Townsy Credit pays only the price, never the tax. Every order has a receipt on the Orders page.",
        "While Townsy shows \"TEST\", payments are made in Stripe test mode with test cards: no real money moves.",
      ],
    },
    {
      heading: "6. Chargebacks and problems",
      paragraphs: [`If something went wrong with a payment, write to ${CONTACT_EMAIL} first. A disputed card payment can lead us to reverse what it bought and to suspend the account while the dispute is open.`],
    },
  ],
};

const advertisers: LegalDoc = {
  slug: "advertisers",
  title: "Advertiser Policy",
  summary: "What can appear on store signs, billboards and sponsored tasks, and how designs are reviewed.",
  sections: [
    {
      heading: "1. What is allowed",
      paragraphs: [
        "Businesses of every category may advertise, except: pornography or sexually explicit content, any content that sexualises minors (reported to the authorities), illegal goods or services, malware, phishing or scams, impersonation, and hate or violent threats.",
        "Some categories (for example gambling and crypto-assets) are marked restricted: they may be shown only where Townsy's settings allow them and the law allows them for the audience.",
      ],
    },
    {
      heading: "2. Your rights to what you upload",
      paragraphs: [
        "You confirm that you own, or have permission to use, every logo, image, name and website you submit. We remove designs on a credible rights complaint and may suspend repeat offenders.",
      ],
    },
    {
      heading: "3. Review",
      paragraphs: [
        "Every design and website link is checked before it appears in the town: by Townsy staff or, where enabled, by an automated review (an AI model) of the design and the website address that looks for adult content, scams, malware and other policy problems. The automated review does not visit your website, and anything it is unsure about goes to a person. If it rejects your design and you think it is wrong, submit the same design again unchanged and a person will review it. We may remove or revoke a design at any time; where it was shown, a neutral sign appears instead.",
      ],
    },
    {
      heading: "4. Sponsored tasks",
      paragraphs: [
        "Tasks reward users with coins for a clear action. They are free for the owner of the store: you never pay for them, and Townsy gives the coins (a virtual reward with no cash value), up to a daily limit per store. Townsy reports rewarded visits separately and never claims more than it can check. A task budget paid before tasks became free runs until it is used and follows the Payments & Credit Policy.",
        "Every task is checked before it can run: by Townsy staff or, where enabled, by the same automated review, which reads the task's text and your website address (it does not visit your website) and leaves anything unclear, and every task confirmed by your own server, to a person. You can remove a task at any time: nobody new can start it and people already doing it may finish. When your store's website changes, your website tasks are renewed for the new address with the same reward and are checked again.",
      ],
    },
  ],
};

const community: LegalDoc = {
  slug: "community",
  title: "Community Rules",
  summary: "How to treat people in Townsy's chat, private messages and public spaces.",
  sections: [
    {
      heading: "1. Be decent",
      paragraphs: ["Do not:"],
      bullets: [
        "harass, bully, threaten or stalk anyone;",
        "post sexual content, hate or slurs (a language filter hides many words, but it is not a licence to try);",
        "scam people, ask for passwords, codes or payment details, or spam links;",
        "pretend to be someone else, including Townsy staff;",
        "share anyone's private information.",
      ],
    },
    {
      heading: "2. Tools you have",
      paragraphs: [
        "Click a person to Mute them (their messages are hidden for you; muted people cannot send you private messages) or Report them. Choose who may send you private messages on your Account page.",
      ],
    },
    {
      heading: "3. What moderators do",
      paragraphs: [
        "Townsy moderators review reports with the recent messages involved and may dismiss them, warn, mute for a period, or ban from Townsy rooms. Serious threats or harm to children are reported to the authorities.",
      ],
    },
  ],
};

export const LEGAL_DOCS: Record<LegalSlug, LegalDoc> = { terms, privacy, payments, advertisers, community };

export function isLegalSlug(v: string | undefined): v is LegalSlug {
  return v === "terms" || v === "privacy" || v === "payments" || v === "advertisers" || v === "community";
}
